/**
 * Warehouse vs office shell mode — drives primary nav density and Home layout.
 * Persisted per browser so Sam's warehouse dock survives reloads.
 */
import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { usePermissions } from '@/composables/usePermissions.js'

const STORAGE_KEY = 'eqsys_shell_mode'

function readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'warehouse' || v === 'office') return v
  } catch { /* ignore */ }
  return null
}

export const useShellModeStore = defineStore('shellMode', () => {
  const mode = ref(readStored() || 'warehouse')
  const isWarehouse = computed(() => mode.value === 'warehouse')
  const isOffice = computed(() => mode.value === 'office')

  function setMode(next) {
    if (next !== 'warehouse' && next !== 'office') return
    mode.value = next
    try { localStorage.setItem(STORAGE_KEY, next) } catch { /* ignore */ }
  }

  function toggle() {
    setMode(mode.value === 'warehouse' ? 'office' : 'warehouse')
  }

  /** Prefer warehouse for operators; office for managers/admins who last chose office. */
  function preferForRole() {
    if (readStored()) return
    const perms = usePermissions()
    if (perms.isAdmin.value || perms.isManagerOrAbove.value) setMode('office')
    else if (perms.isOperatorOrAbove.value) setMode('warehouse')
  }

  watch(mode, (v) => {
    try { localStorage.setItem(STORAGE_KEY, v) } catch { /* ignore */ }
  })

  return { mode, isWarehouse, isOffice, setMode, toggle, preferForRole }
})
